import React, { useState, useMemo, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Search, Filter, ArrowUp, ArrowDown, Package } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../Components/UI/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/UI/table";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from "../../Components/UI/drawer";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import { cn } from "../../Lib/Cn";
import { RiResetLeftFill } from "react-icons/ri";
import useIsMobile from "../../Hooks/useIsMobile";
import { getProductAnalyticsAPI } from "../Threads/ThreadsAPI";
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import {
  getPastMonthOptions,
  months,
  getDateRangeOptions,
  getPastQuarterOptions,
  getOtherYearOptions,
  currentUserHasPermission,
} from "../../Utils/Utilities";
import { useSelector } from "react-redux";
import { getUsersArr, isSmartSellerMode } from "../../Store/Reducers";
import ThreadDropdown from "../../Modules/Threads/AddThreadDropdown";
import { TooltipProvider } from "../../Components/UI/tooltip";
import { useLocation } from "react-router-dom";
import Pagination from "../../Components/Common/Pagination";
import { formatAmount, FormatAmount } from "../../Utils/Utilities";
import { useCurrency } from "../../Hooks/useCurrency";
import { format } from "date-fns";
import { useRef } from "react";
import { startOfDay, subDays } from "date-fns";

// Add styles for rounded legend
const legendStyle = `
  .recharts-legend-wrapper svg rect {
    rx: 4 !important;
    ry: 4 !important;
  }
  /* Remove black focus box on click */
  *:focus {
    outline: none !important;
  }
  .recharts-surface:focus {
    outline: none !important;
  }
`;
const getCurrentMonthYear = () => format(new Date(), "MMMM yyyy");
const getTodayDateStr = () => format(new Date(), "yyyy-MM-dd");
const getCurrentQuarterYear = () => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q} ${now.getFullYear()}`;
};
// Theme Colors matching Sahi CRM
const themeColors = [
  "#0c6980",
  "#0b5d1e",
  "#d4af37",
  "#bf5700",
  "#230e2eff",
  "#c44536",
  "#2a9d8f",
  "#46210fff",
  "#3d348b",
  "#264653",
];

const ProductDemandReport = () => {
  const isMobile = useIsMobile();
  const users = useSelector(getUsersArr);
  const location = useLocation();
  const currencySymbol = useCurrency();
  const [threadTypeID, setThreadTypeID] = useState(1);
  // Inside your component
  const activeData = useSelector((state) => state.activeData);
  const thrViewTypeID = currentUserHasPermission('ThrViewTypeID');
  const isAssignedOnly = thrViewTypeID === 2;

  const monthlyJson = useMemo(() => {
    const raw = activeData.data.MonthlyThreads;
    // If it's a string, parse it; if it's already an array, use it; otherwise empty array
    if (typeof raw === 'string') return JSON.parse(raw);
    if (Array.isArray(raw)) return raw;
    return [];
  }, [activeData.data.MonthlyThreads]);

  const availablePeriods = useMemo(() => {
    const monthsSet = new Set();
    const quartersSet = new Set();
    const yearsSet = new Set();

    const now = new Date();
    const currentMonthName = months[now.getMonth()];
    const currentYear = now.getFullYear();

    // 1. Add current periods by default
    monthsSet.add(`${currentMonthName} ${currentYear}`);
    quartersSet.add(`Q${Math.ceil((now.getMonth() + 1) / 3)} ${currentYear}`);
    yearsSet.add(currentYear.toString());

    // 2. Extract from JSON
    if (Array.isArray(monthlyJson)) {
      monthlyJson.forEach(item => {
        if (item.YY && item.MM) {
          const y = item.YY.toString();
          const mName = months[item.MM - 1];
          const q = Math.ceil(item.MM / 3);
          if (mName) monthsSet.add(`${mName} ${y}`);
          quartersSet.add(`Q${q} ${y}`);
          yearsSet.add(y);
        }
      });
    }

    // 3. SORTING LOGIC: Newest to Oldest
    const sortedMonths = Array.from(monthsSet).sort((a, b) => {
      return new Date(b) - new Date(a);
    });

    const sortedQuarters = Array.from(quartersSet).sort((a, b) => {
      const [qA, yA] = a.split(" ");
      const [qB, yB] = b.split(" ");
      return yB !== yA ? parseInt(yB) - parseInt(yA) : qB.localeCompare(qA);
    });

    const sortedYears = Array.from(yearsSet).sort((a, b) => parseInt(b) - parseInt(a));

    return {
      months: sortedMonths,
      quarters: sortedQuarters,
      years: sortedYears,
    };
  }, [monthlyJson]);


  const { data: allConns = [] } = useSelector((state) => state.connectionData);
  const searchParams = new URLSearchParams(location.search);
  const executiveParam = searchParams.get("executive");
  const dateRangeParam = searchParams.get("dateRange");
  const otherMonthParam = searchParams.get("otherMonth");
  const otherQuarterParam = searchParams.get("otherQuarter");
  const otherYearParam = searchParams.get("otherYear");
  const monthParam = searchParams.get("m"); // month number from URL

  // Helper function to parse month parameter (like ThreadsReport)
  const currentDate = new Date();
  const currentMonth = months[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const monthKey = `${currentMonth} ${currentYear}`;

  const getMonthFilterFromNumber = (monthNumber) => {
    if (!monthNumber) {
      return {
        dateRange: 'This Month',
        otherMonth: monthKey
      };
    }

    const monthIndex = parseInt(monthNumber, 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) {
      return {
        dateRange: 'This Month',
        otherMonth: monthKey
      };
    }

    const passedYear = currentYear;

    if (monthIndex === currentDate.getMonth() && passedYear === currentYear) {
      return {
        dateRange: 'This Month',
        otherMonth: monthKey
      };
    }

    const monthName = months[monthIndex];

    return {
      dateRange: 'Other Month',
      otherMonth: `${monthName} ${currentYear}`
    };
  };
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    const now = new Date();

    return {
      executive: params.get("eid") || "all",
      connID: params.get("cid") || "all",
      dateRange: params.get("p") || "This Month",
      otherMonth: params.get("om") || getCurrentMonthYear(),
      otherQuarter: params.get("oq") || getCurrentQuarterYear(),
      // FIX: Pre-select current year instead of "all"
      otherYear: params.get("oy") || now.getFullYear().toString(),
      otherDate: params.get("od") || getTodayDateStr(),
      product: "all"
    };
  };


  const prevFiltersRef = useRef({}); // Essential for loop prevention
  // Helper function to parse month parameter from URL
  const getFilterFromParams = () => {
    const params = new URLSearchParams(location.search);
    const now = new Date();

    // Format current date strings correctly
    const currentMonthYear = format(now, "MMMM yyyy");
    const currentQuarterYear = `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`;
    const currentYearStr = now.getFullYear().toString();

    const monthFilters = getMonthFilterFromNumber(params.get("m"));

    return {
      dateRange: params.get("p") || "This Month",
      // Priority: URL param -> Calculated from 'm' param -> Current Month string
      otherMonth: params.get("om") || monthFilters.otherMonth || currentMonthYear,
      otherQuarter: params.get("oq") || currentQuarterYear,
      otherYear: params.get("oy") || currentYearStr,
      executive: params.get("eid") || "all",
      product: "all"
    };
  };

  // State Management
  const [valueType, setValueType] = useState('count');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterSelected, setIsFilterSelected] = useState(isMobile ? false : true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fix #4: Default Desktop Sort set to 'totalValue' descending
  const [sortConfig, setSortConfig] = useState({
    key: "totalValue",
    direction: "desc",
  });

  const [hasActiveFilters, setHasActiveFilters] = useState(
    !!(executiveParam || dateRangeParam || otherMonthParam || otherQuarterParam || otherYearParam || monthParam)
  );

  // Pagination State
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter state
  const [selectedFilters, setSelectedFilters] = useState(getFilterFromParams());
  const [tempFilters, setTempFilters] = useState(selectedFilters);

  // Fix #2: Sort Products Alphabetically for filter
  const products = [...new Set(rawData.map((item) => item.Product))].sort(
    (a, b) => a.localeCompare(b)
  );
  const oldestSinceDT = useMemo(() => {
    if (!allConns || allConns.length === 0) return new Date();

    const dates = allConns
      .map((conn) => {
        const dateStr = conn.SinceDT || conn.CreatedDT;
        return dateStr ? new Date(dateStr) : null;
      })
      .filter((d) => d !== null && !isNaN(d));

    if (dates.length === 0) return new Date();

    // Find the absolute oldest date across all connections
    return new Date(Math.min(...dates));
  }, [allConns]);
  // Filter Configuration Array
  const filterOptions = [
    {
      key: "dateRange",
      label: "Period",
      // Specific order requested: Month -> Quarter -> Year
      options: [
        "This Month",
        "Other Month",
        "This Quarter",
        "Other Quarter",
        "This Year",
        "Other Year"
      ],
      searchEnabled: false,
      type: "options",
      showAllOption: false, // Usually set to false for strict period selection
      mobileLabel: "Period",
    },
    {
      key: "otherMonth",
      label: "Select Month",
      // FIX: Use availablePeriods instead of getPastMonthOptions
      options: availablePeriods.months,
      disabled: !(selectedFilters.dateRange === "Other Month" || tempFilters.dateRange === "Other Month"),
      mobiledisabled: !(tempFilters.dateRange === "Other Month"),
      type: "list",
      mobileDropdownText: "Select Month",
      mobileNoLabel: true,
      isSorting: false
    },
    {
      key: "otherQuarter",
      label: "Select Quarter",
      // FIX: Use availablePeriods instead of getPastQuarterOptions
      options: availablePeriods.quarters,
      disabled: !(selectedFilters.dateRange === "Other Quarter" || tempFilters.dateRange === "Other Quarter"),
      mobiledisabled: !(tempFilters.dateRange === "Other Quarter"),
      type: "list",
      mobileDropdownText: "Select Quarter",
      mobileNoLabel: true,
      isSorting: false,
    },
    {
      key: "otherYear",
      label: "Select Year",
      // FIX: Use availablePeriods instead of getOtherYearOptions
      options: availablePeriods.years,
      disabled: !(selectedFilters.dateRange === "Other Year" || tempFilters.dateRange === "Other Year"),
      mobiledisabled: !(tempFilters.dateRange === "Other Year"),
      type: "list",
      displayValue: (val) => val === "all" ? "Select Year" : val,
      mobileDropdownText: "Select Year",
      mobileNoLabel: true,
      isSorting: false
    },
    ...(!isSmartSellerMode() ? [{
      key: "executive",
      label:
        selectedFilters.executive === "unassigned"
          ? "Not Assigned"
          : selectedFilters.executive === "all"
            ? "All Executives"
            : users.find(
              (u) =>
                u.userID.toString() === selectedFilters.executive.toString()
            )?.name || "All Executives",
      options: [
        { name: "Not Assigned", ID: "unassigned" },
        ...users
          .map((u) => ({ name: u.name, ID: u.userID }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ],
      displayValue: (val) => {
        if (val === "all") return "All Executives";
        if (val === "unassigned") return "Not Assigned";
        const foundUser = users.find(
          (u) => u.userID.toString() === val.toString()
        );
        return foundUser?.name || "All Executives";
      },
      type: "list",
      mobileDropdownText: "Select Executive",
      MobileLabel: "Executive",
      showAllOption: true,
      mobileLabel: "Executive",
    }] : []),

  ];

  const fetchProductAnalytics = async () => {
    try {
      const now = new Date();
      // 1. Calculate Resolved Parameters first
      let year = now.getFullYear();
      let month = now.getMonth() + 1;
      let prdType = "MM";
      let prdQty = 1;

      const p = selectedFilters.dateRange;

      if (p === "This Month") {
        prdType = "MM";
      } else if (p === "Other Month") {
        const monthToUse = selectedFilters.otherMonth || getCurrentMonthYear();
        const [mName, yStr] = monthToUse.split(" ");
        month = months.indexOf(mName) + 1;
        year = parseInt(yStr);
        prdType = "MM";
      } else if (p === "This Quarter") {
        const qtr = Math.ceil(month / 3);
        prdType = `QQ${qtr}`;
        month = 0; // Standard for Quarter queries
      } else if (p === "Other Quarter") {
        const qVal = selectedFilters.otherQuarter === "all" ? getCurrentQuarterYear() : selectedFilters.otherQuarter;
        const [qStr, yStr] = qVal.split(" ");
        prdType = `QQ${qStr.replace("Q", "")}`;
        year = parseInt(yStr);
        month = 0;
      } else if (p === "This Year") {
        prdType = "YY";
        month = 0; // Standard for Year queries
      } else if (p === "Other Year") {
        prdType = "YY";
        year = parseInt(selectedFilters.otherYear);
        month = 0;
      } else if (p === "Last Month") {
        const lm = subDays(startOfDay(now), 30);
        month = lm.getMonth() + 1;
        year = lm.getFullYear();
        prdType = "MM";
      }

      // 2. CHECK GUARD: If these exact DB parameters were already fetched, STOP.
      const prev = prevFiltersRef.current;
      if (
        prev.prdType === prdType &&
        prev.year === year &&
        prev.month === month &&
        prev.executive === selectedFilters.executive &&
        prev.threadTypeID === threadTypeID &&
        rawData.length > 0
      ) {
        console.log("Skipping API call: Data already loaded for this period");
        return;
      }

      // 3. Proceed with API Call
      setIsLoading(true);

      const payload = {
        prdType,
        prdQty,
        year: year || currentDate.getFullYear(),
        month,
        threadTypeID,
      };

      const result = await getProductAnalyticsAPI(payload);
      checkAPIResponse(result);

      if (result && result.updatePackage) {
        const updtPkg = JSON.parse(result.updatePackage);
        const response = updtPkg.Table?.[0];

        if (response && parseInt(response.FnErrCode) !== -1) {
          setRawData([]);
        } else {
          const productAnalysis = updtPkg.Table1 || [];
          const transformedData = productAnalysis
            .map((item) => {
              if (!item.MainProduct || item.MainProduct.trim() === "") return null;
              return {
                Product: item.MainProduct,
                POCUserID: item.POCUserID,
                ThreadCount: Number(item.CntThread || 0),
                ThreadIDs: item.ThreadIDs,
                MainProduct: item.MainProduct,
                BizProspect: Number(item.BizProspect || 0),
                MainQty: Number(item.CntThread || 0),
              };
            })
            .filter((item) => item !== null);

          setRawData(transformedData);

          // 4. UPDATE REF with the resolved API params to prevent future redundant calls
          prevFiltersRef.current = {
            prdType,
            year,
            month,
            executive: selectedFilters.executive,
            threadTypeID,
          };
        }
      }
    } catch (error) {
      console.error("Error fetching product analytics:", error);
      setRawData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Product Demand";

    return () => {
      document.title = "SahiClub";
    };
  }, []);
  // Listen for mobile header filter click
  useEffect(() => {
    const handleHeaderFilterClick = () => {
      setIsFilterSelected(true);
      setIsDrawerOpen(true);
    };
    window.addEventListener('open-product-demand-filter', handleHeaderFilterClick);
    return () => window.removeEventListener('open-product-demand-filter', handleHeaderFilterClick);
  }, []);

  useEffect(() => {
    fetchProductAnalytics();
  }, [selectedFilters, threadTypeID]);

  useEffect(() => {
    const newFiltersFromUrl = getFilterFromParams(); // Use the helper created above

    // Deep compare to avoid infinite loops
    const isDifferent = Object.keys(newFiltersFromUrl).some(
      key => String(newFiltersFromUrl[key]) !== String(selectedFilters[key])
    );

    if (isDifferent) {
      setSelectedFilters(newFiltersFromUrl);
      setTempFilters(newFiltersFromUrl); // Critical for mobile pre-selection
    }
  }, [location.search]);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Check for active filters (for mobile green dot)
  useEffect(() => {
    const hasActive =
      selectedFilters.executive !== 'all' ||
      selectedFilters.product !== 'all' ||
      selectedFilters.dateRange !== 'This Month';
    setHasActiveFilters(hasActive);
  }, [selectedFilters]);

  // Process raw data for display
  const processedData = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return {
        data: [],
        total: 0,
        totalValue: 0,
        filteredCount: 0,
        rawTotal: 0,
      };
    }

    let filtered = rawData;

    // UPDATED EXECUTIVE FILTER LOGIC
    if (tempFilters.executive !== "all") {
      if (tempFilters.executive === "unassigned") {
        // Filter for items where POCUserID is missing or 0
        filtered = filtered.filter(
          (item) =>
            !item.POCUserID || item.POCUserID === 0 || item.POCUserID === "0"
        );
      } else {
        // Filter for specific Executive ID
        filtered = filtered.filter(
          (item) => String(item.POCUserID) === String(tempFilters.executive)
        );
      }
    }

    if (tempFilters.product !== 'all') {
      filtered = filtered.filter(item => item.Product === tempFilters.product);
    }

    // Group filtered data by product
    const productMap = {};
    filtered.forEach(item => {
      const product = item.MainProduct || 'Unknown';
      if (!productMap[product]) {
        productMap[product] = {
          product,
          count: 0,
          totalValue: 0,
          totalQty: 0,
          executives: []
        };
      }
      productMap[product].count += Number(item.ThreadCount || 0);
      productMap[product].totalValue += Number(item.BizProspect || 0);
      productMap[product].totalQty += Number(item.ThreadCount || 0);
      if (item.POCUserID) {
        productMap[product].executives.push(item.POCUserID);
      }
    });

    const aggregated = Object.values(productMap);
    const filteredTotalCount = aggregated.reduce(
      (sum, item) => sum + item.count,
      0
    );
    const filteredTotalValue = aggregated.reduce(
      (sum, item) => sum + item.totalValue,
      0
    );

    const withPercentages = aggregated.map((item) => {
      const countPercentage =
        filteredTotalCount > 0
          ? ((item.count / filteredTotalCount) * 100).toFixed(2)
          : 0;
      const valuePercentage =
        filteredTotalValue > 0
          ? ((item.totalValue / filteredTotalValue) * 100).toFixed(2)
          : 0;

      return {
        ...item,
        countPercentage,
        valuePercentage,
      };
    });

    const searchFiltered = searchTerm
      ? withPercentages.filter((item) =>
        item.product.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : withPercentages;

    const chartData = aggregated.map((item) => {
      const countPercentage =
        filteredTotalCount > 0
          ? ((item.count / filteredTotalCount) * 100).toFixed(2)
          : 0;
      const valuePercentage =
        filteredTotalValue > 0
          ? ((item.totalValue / filteredTotalValue) * 100).toFixed(2)
          : 0;

      return {
        product: item.product,
        count: item.count,
        totalValue: item.totalValue,
        countPercentage,
        valuePercentage,
      };
    });

    return {
      data: searchFiltered,
      chartData: chartData,
      totalCount: filteredTotalCount,
      totalValue: filteredTotalValue,
      filteredCount: filtered.length,
      rawTotal: filteredTotalCount,
    };
  }, [rawData, tempFilters, valueType, searchTerm]);  // Pagination logic
  const renderEmptyState = () => {
    const config = {
      icon: Package,
      description: "Analyze product demand for both inquiries and orders.",
      subText: "No data available right now.",
    };

    const IconComponent = config.icon;

    return (
      <div className="w-full">
        <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white shadow-sm w-full md:w-fit md:min-w-[500px]">
          <div className="flex-shrink-0 p-2 bg-gray-50 rounded-md">
            <IconComponent className="h-6 w-6 text-gray-400" strokeWidth={2} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-gray-700 leading-tight">
              {config.description}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {config.subText}
            </span>
          </div>
        </div>
      </div>
    );
  };
  // Helper to calculate dynamic height for charts
  const getDynamicChartHeight = (data) => {
    const baseChartHeight = isMobile ? 220 : 200; // Radius space for the actual Pie
    const legendRowHeight = isMobile ? 25 : 20;   // Estimated height per legend row
    const itemsPerRow = isMobile ? 2 : 3;         // Items per horizontal row
    const numRows = Math.ceil((data?.length || 0) / itemsPerRow);

    return baseChartHeight + (numRows * legendRowHeight);
  };

  const dynamicHeight = useMemo(() =>
    getDynamicChartHeight(processedData.chartData),
    [processedData.chartData, isMobile]
  );


  // Pagination & Sorting Logic
  const currentData = useMemo(() => {
    if (!processedData.data || processedData.data.length === 0) return [];

    let dataToSort = [...processedData.data];

    // Fix #5: Mobile specific sorting based on Tab (valueType)
    if (isMobile) {
      if (valueType === "count") {
        // Sort by Count Descending
        dataToSort.sort((a, b) => Number(b.count) - Number(a.count));
      } else {
        // Sort by Value Descending
        dataToSort.sort((a, b) => Number(b.totalValue) - Number(a.totalValue));
      }
    } else {
      // Fix #4: Desktop Sorting based on sortConfig
      if (sortConfig.key) {
        dataToSort = dataToSort.sort((a, b) => {
          let aValue = a[sortConfig.key] || '';
          let bValue = b[sortConfig.key] || '';

          // Numeric sort
          if (
            [
              "count",
              "totalValue",
              "countPercentage",
              "valuePercentage",
            ].includes(sortConfig.key)
          ) {
            aValue = parseFloat(aValue) || 0;
            bValue = parseFloat(bValue) || 0;
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
          }

          // String sort
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }
    }

    if (isMobile) {
      return dataToSort;
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return dataToSort.slice(startIndex, endIndex);
  }, [
    processedData.data,
    sortConfig,
    currentPage,
    rowsPerPage,
    isMobile,
    valueType,
  ]);

  // Handle new thread (Inquiry - TID 1)
  const handleThreadTypeSelect = (threadTypeID) => {
    // Only allow TID 1 (Inquiry)
    if (threadTypeID === 1) {
      fetchProductAnalytics();
    }
  };

  // Handle filter apply
  const handleApplyFilters = () => {
    setSelectedFilters(tempFilters);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
  };

  // Handle cancel filters
  const handleCancelFilters = () => {
    setTempFilters(selectedFilters);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    const now = new Date();
    const resetFilters = {
      period: 'This Month',
      dateRange: 'This Month',
      otherMonth: format(now, "MMMM yyyy"),
      otherQuarter: `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`,
      otherYear: now.getFullYear().toString(), // FIX: Reset to current year
      executive: 'all',
      product: 'all'
    };
    setTempFilters(resetFilters);
    setSelectedFilters(resetFilters);
    setThreadTypeID(1);
    setValueType('count');
    setSearchTerm('');
    setSortConfig({ key: "totalValue", direction: "desc" });

    // Set this to true if you want the filter bar to stay open on desktop after reset
    setIsFilterSelected(true);

    window.history.pushState({}, "", window.location.pathname);
  };

  // Table Component with pagination
  const ReportTable = () => (
    <div className="space-y-4">
      <div className={cn("overflow-x-auto", isMobile && "overflow-x-hidden")}>
        {isMobile ? (
          // Mobile Grid Layout
          <div className="w-full">
            <div className="grid grid-cols-[45%_25%_30%] bg-gray-50 py-1.5 h-11 border-b">
              <div className="font-semibold text-sm text-black pl-4 flex items-center">
                Product
              </div>
              {valueType === "count" ? (
                <>
                  <div className="font-semibold text-sm text-black flex items-center justify-end pr-2">
                    Count
                  </div>
                  <div className="font-semibold text-sm text-black flex items-center justify-end pr-3">
                    Share
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold text-sm text-black flex items-center justify-end pr-2">
                    Value
                  </div>
                  <div className="font-semibold text-sm text-black flex items-center justify-end pr-3">
                    Share
                  </div>
                </>
              )}
            </div>
            <div className="divide-y divide-gray-200">
              {currentData.map((item, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[45%_25%_30%] hover:bg-gray-50 h-10 items-center"
                >
                  <div className="pl-4 truncate text-sm text-text-color">
                    {item.product}
                  </div>
                  {valueType === "count" ? (
                    <>
                      <div className="text-right pr-2 text-sm text-text-color">
                        {item.count}
                      </div>
                      <div className="text-right pr-3 text-sm text-text-color">
                        {item.countPercentage}%
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-right pr-2 text-sm text-text-color">
                        {formatAmount(item.totalValue)}
                      </div>
                      <div className="text-right pr-3 text-sm text-text-color">
                        {item.valuePercentage}%
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* Mobile Total Row */}
            <div className="grid grid-cols-[45%_25%_30%] bg-gray-50 h-11 items-center border-t border-gray-200">
              <div className="pl-4 text-sm text-black font-semibold">
                Total
              </div>
              {valueType === "count" ? (
                <>
                  <div className="text-right pr-2 text-sm text-black font-semibold">
                    {processedData.totalCount}
                  </div>
                  <div className="text-right pr-3 text-sm text-black font-semibold">
                    100.00%
                  </div>
                </>
              ) : (
                <>
                  <div className="text-right pr-2 text-sm text-black font-semibold">
                    {formatAmount(processedData.totalValue)}
                  </div>
                  <div className="text-right pr-3 text-sm text-black font-semibold">
                    100.00%
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          /* Desktop Table */
          <Table className="min-w-full table-auto">
            <TableHeader>
              {/* CHANGE bg-bg-white TO bg-gray-50/50 */}
              <TableRow className="bg-gray-50/50 py-1.5 h-11 border-b">
                <TableHead
                  className="text-left pl-4 font-semibold text-sm text-black cursor-pointer"
                  onClick={() => {
                    let direction = sortConfig.key === "product" && sortConfig.direction === "asc" ? "desc" : "asc";
                    setSortConfig({ key: "product", direction });
                  }}
                >
                  <div className="flex items-center">
                    Product{" "}
                    {sortConfig.key === "product" &&
                      (sortConfig.direction === "asc" ? (
                        <ArrowUp className="ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="ml-1 h-4 w-4" />
                      ))}
                  </div>
                </TableHead>

                {/* Count Header - Right Aligned */}
                <TableHead
                  className="text-right pr-4 font-semibold text-sm text-black cursor-pointer w-[12%]"
                  onClick={() =>
                    setSortConfig({
                      key: "count",
                      direction: sortConfig.direction === "asc" ? "desc" : "asc",
                    })
                  }
                >
                  <div className="flex items-center justify-end">
                    Count{" "}
                    {sortConfig.key === "count" &&
                      (sortConfig.direction === "asc" ? (
                        <ArrowUp className="ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="ml-1 h-4 w-4" />
                      ))}
                  </div>
                </TableHead>

                {/* Count Share - Right Aligned */}
                <TableHead className="text-right pr-4 font-semibold text-sm text-black w-[12%]">
                  Share
                </TableHead>

                {/* Value Header - Right Aligned */}
                <TableHead
                  className="text-right pr-4 font-semibold text-sm text-black cursor-pointer w-[18%]"
                  onClick={() =>
                    setSortConfig({
                      key: "totalValue",
                      direction: sortConfig.direction === "asc" ? "desc" : "asc",
                    })
                  }
                >
                  <div className="flex items-center justify-end">
                    Value ({currencySymbol}){" "}
                    {sortConfig.key === "totalValue" &&
                      (sortConfig.direction === "asc" ? (
                        <ArrowUp className="ml-1 h-4 w-4" />
                      ) : (
                        <ArrowDown className="ml-1 h-4 w-4" />
                      ))}
                  </div>
                </TableHead>

                {/* Value Share - Right Aligned */}
                <TableHead className="text-right pr-6 font-semibold text-sm text-black w-[12%]">
                  Share
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {currentData.map((item, idx) => (
                <TableRow key={idx} className="hover:bg-gray-50 border-b h-10">
                  <TableCell className="text-left pl-4 max-w-[250px] truncate text-sm text-text-color">
                    {item.product}
                  </TableCell>
                  {/* Right Aligned Data Cells */}
                  <TableCell className="text-right pr-4 text-sm text-text-color">
                    {item.count}
                  </TableCell>
                  <TableCell className="text-right pr-4 text-sm text-text-color">
                    {item.countPercentage}%
                  </TableCell>
                  <TableCell className="text-right pr-4 text-sm text-text-color">
                    <FormatAmount
                      amount={item.totalValue || 0}
                      currency={currencySymbol}
                      showCurr={false}
                    />
                  </TableCell>
                  <TableCell className="text-right pr-6 text-sm text-text-color">
                    {item.valuePercentage}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {/* Footer - Semi-bold (font-semibold) matching Header styling */}
            <tfoot className="bg-gray-50/50 border-t border-gray-200">
              <TableRow className="h-11 hover:bg-transparent">
                <TableCell className="text-left pl-4 font-semibold text-sm text-black align-middle">
                  Total
                </TableCell>
                <TableCell className="text-right pr-4 font-semibold text-sm text-black align-middle">
                  {processedData.totalCount}
                </TableCell>
                <TableCell className="text-right pr-4 font-semibold text-sm text-black align-middle">
                  100.00%
                </TableCell>
                {/* Desktop Table Footer - Replace the Total Value TableCell */}
                <TableCell className="text-right pr-4 font-semibold text-sm text-black align-middle">
                  <FormatAmount
                    amount={processedData.totalValue || 0}
                    currency={currencySymbol}
                    showCurr={false}
                  />
                </TableCell>
                <TableCell className="text-right pr-6 font-semibold text-sm text-black align-middle">
                  100.00%
                </TableCell>
              </TableRow>
            </tfoot>
          </Table>)}
      </div>
    </div>
  );
  const showCharts = processedData.data.length > 1;

  return (
    <TooltipProvider>
      <style>{legendStyle}</style>
      <div className={`lg:space-y-6 overflow-x-hidden pb-4`}>
        {/* Web Layout */}
        <div className="hidden lg:flex justify-between items-center gap-3 h-12">
          <div className="flex items-center flex-grow">
            <h1 className="text-3xl font-bold text-text-color mr-8 web-header-name">
              Product Demand
            </h1>
            <div className="flex items-center justify-end flex-grow gap-3">
              {processedData.data.length > 0 && (
                <>
                  {/* <div
                    className={cn(
                      "flex items-center px-3 lg:px-4 h-8 lg:h-10",
                      "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm"
                    )}
                  >
                    <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                      <span>Total: </span>
                      <span className="font-medium">
                        {processedData.totalCount}
                      </span>
                    </span>
                  </div> */}
                  {processedData.data.length > 10 && (
                    <div className="relative w-120 filter-border">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                      <Input
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                      />
                    </div>
                  )}
                  {/* <ThreadDropdown
                    onThreadTypeSelect={handleThreadTypeSelect}
                    iconButton={false}
                    className='!text-base'
                    activeFilteredTypes="1"
                  /> */}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 h-full">
            {/* Thread Type Toggle */}
            <div className="bg-[#f4f4f5] p-1 rounded-[5px] flex items-center border border-gray-200 h-8 lg:h-10">
              <button
                type="button"
                onClick={() => setThreadTypeID(1)}
                className={cn(
                  "h-full px-3.5 text-sm font-normal rounded-[4px] transition-all flex items-center justify-center",
                  threadTypeID === 1
                    ? "bg-white text-black shadow-xs"
                    : "text-gray-500 hover:text-black"
                )}
              >
                Inquiries
              </button>
              <button
                type="button"
                onClick={() => setThreadTypeID(2)}
                className={cn(
                  "h-full px-3.5 text-sm font-normal rounded-[4px] transition-all flex items-center justify-center",
                  threadTypeID === 2
                    ? "bg-white text-black shadow-xs"
                    : "text-gray-500 hover:text-black"
                )}
              >
                Sales
              </button>
            </div>

            <Button
              className={cn(
                "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] relative",
                isFilterSelected && "filter-active"
              )}
              onClick={() => setIsFilterSelected(prev => !prev)}
              aria-label="Toggle filter"
            >
              <div className="h-6 w-6 flex items-center justify-center transition-all">
                <Filter
                  className={cn(
                    "h-4 w-4",
                    isFilterSelected
                      ? "text-sahi-primary-color"
                      : "text-white group-hover:text-sahi-primary-color"
                  )}
                />
              </div>
            </Button>
          </div>
        </div>

        {/*--------------- Filter Section (Desktop) ------------*/}
        {isFilterSelected && !isMobile && (
          <div className="hidden lg:flex flex-wrap gap-4 items-center mb-2">
            {/* Period Filter Component */}
            <PeriodFilter
              value={selectedFilters}
              onChange={(newFilters) => {
                setSelectedFilters(newFilters);
                setTempFilters(newFilters);
              }}
              monthlyData={monthlyJson}
              showMonthFilter={true}
              showQuarterFilter={true}
              showYearFilter={true}
            />

            {/* Executive Filter */}
            {!isSmartSellerMode() && !isAssignedOnly ? <>
              <CommonFilter
                key="executive"
                label={
                  selectedFilters.executive === "unassigned"
                    ? "Not Assigned"
                    : selectedFilters.executive === "all"
                      ? "All Executives"
                      : users.find(
                        (u) =>
                          u.userID.toString() === selectedFilters.executive.toString()
                      )?.name || "All Executives"
                }
                options={[
                  { name: "Not Assigned", ID: "unassigned" },
                  ...users
                    .map((u) => ({ name: u.name, ID: u.userID }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
                ]}
                value={selectedFilters.executive}
                onChange={(value) => {
                  setSelectedFilters((prev) => ({ ...prev, executive: value }));
                  setTempFilters((prev) => ({ ...prev, executive: value }));
                }}
                displayValue={(val) => {
                  if (val === "all") return "All Executives";
                  if (val === "unassigned") return "Not Assigned";
                  const foundUser = users.find(
                    (u) => u.userID.toString() === val.toString()
                  );
                  return foundUser?.name || "All Executives";
                }}
                type="list"
                mobileLabel="Executive"
              /> </> : null}
          </div>
        )}

        {/*--------------- Filter Drawer (Mobile) ------------*/}
        {isFilterSelected && isMobile && (
          <Drawer open={isDrawerOpen} onOpenChange={() => {
            setIsDrawerOpen(false);
            setIsFilterSelected(false);
          }}>
            <DrawerContent className="p-0 [&>div:first-child]:hidden">
              <div className="f-col h-[100dvh] !rounded-t-[10px]">
                {/* Header */}
                <div className="flex plain items-center justify-between p-4 border-b-2 border-gray-200 text-xl !rounded-t-[10px]">
                  <p className="text-lg font-semibold text-sahi-primary-color">
                    Filters & Sorting
                  </p>
                  <button
                    className="group flex items-center gap-1 text-sm font-light hover:underline transition duration-200 text-sahi-primary-color"
                    onClick={() => {
                      handleResetFilters();
                      setIsFilterSelected(false);
                    }}
                  >
                    <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                    <span>Reset</span>
                  </button>
                </div>
                {/* Main Content */}
                <div className="flex flex-1 overflow-y-auto">
                  <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
                    {filterOptions.map((filter) => {
                      // 1. Create a local copy so we don't mutate the original source
                      let currentFilter = { ...filter };

                      // 2. FORCE THE OPTION ORDER for the 'dateRange' filter
                      if (currentFilter.key === "dateRange" && Array.isArray(currentFilter.options)) {
                        const preferredOrder = [
                          "This Month",
                          "Other Month",
                          "This Quarter",
                          "Other Quarter",
                          "This Year",
                          "Other Year"
                        ];

                        // Sort the options based on their index in our preferredOrder array
                        currentFilter.options = [...currentFilter.options].sort((a, b) => {
                          return preferredOrder.indexOf(a) - preferredOrder.indexOf(b);
                        });
                      }

                      const { key, label, options, displayValue, disabled, mobiledisabled, mobileDropdownText, MobileLabel, showAllOption, type } = currentFilter;
                      // Only show sub-filters when their parent dateRange is selected
                      const shouldShow =
                        key === "dateRange" ||
                        key === "executive" ||
                        (key === "otherMonth" && tempFilters.dateRange === "Other Month") ||
                        (key === "otherQuarter" && tempFilters.dateRange === "Other Quarter") ||
                        (key === "otherYear" && tempFilters.dateRange === "Other Year");

                      if (!shouldShow) return null;

                      return (
                        <div key={key} id={key} className="mb-4">
                          <DrawerFilter
                            id={key}
                            label={label}
                            options={options}
                            value={tempFilters[key]}
                            displayValue={displayValue}
                            onChange={(value) => {
                              setTempFilters((prev) => ({
                                ...prev,
                                [key]: value,
                              }));
                            }}
                            disabled={mobiledisabled || disabled}
                            type={type || "list"}
                            mobileDropdownText={mobileDropdownText}
                            MobileLabel={MobileLabel}
                            showAllOption={showAllOption}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Footer */}
                <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-bg-white">
                  <DrawerClose>
                    <div className="f-row justify-start gap-2">
                      <Button
                        className="sahi-primary-button"
                        onClick={handleApplyFilters}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="sahi-secondary-button"
                        onClick={handleCancelFilters}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {/* -------------------- MAIN CONTENT AREA -------------------- */}

        {/* Mobile View Toggle */}
        {isMobile && (
          <div className="px-3 py-3 w-full lg:hidden">
            <div className="grid grid-cols-2 bg-zinc-200/80 rounded-md border border-zinc-300/70 p-0.5 w-full">
              <button
                type="button"
                onClick={() => setThreadTypeID(1)}
                className={cn(
                  "py-1.5 text-sm font-normal rounded transition-all text-center",
                  threadTypeID === 1
                    ? "bg-sahi-white text-zinc-900 shadow-xs border border-zinc-200/80"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/50"
                )}
              >
                Inquiries
              </button>
              <button
                type="button"
                onClick={() => setThreadTypeID(2)}
                className={cn(
                  "py-1.5 text-sm font-normal rounded transition-all text-center",
                  threadTypeID === 2
                    ? "bg-sahi-white text-zinc-900 shadow-xs border border-zinc-200/80"
                    : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200/50"
                )}
              >
                Sales
              </button>
            </div>
          </div>
        )}

        {/* --- EMPTY STATE HANDLING (UPDATED) --- */}
        {processedData.data.length === 0 && (
          <div className={cn("flex flex-col items-start pt-6 w-full lg:pt-0", isMobile ? "px-3 pt-3" : "px-0")}>            {!hasActiveFilters && !searchTerm ? (
            /* CASE 1: True Empty State (No filters, No data in DB) */
            renderEmptyState()
          ) : (
            // CASE 2: Filtered Empty State (User searched/filtered and found nothing)
            <div className={cn("flex flex-col items-start  w-full lg:pt-0 px-0")}>
              <div className="f-col items-start gap-5 mt-0 lg:pt-0 lg:px-0 w-full">
                <div className="no-records border border-gray-200 shadow-sm sahi-radius p-4 bg-white flex items-center w-full md:w-fit">
                  <span className="text-gray-600 text-sm font-medium">
                    No records found for the selected filters.
                  </span>
                  <button
                    onClick={handleResetFilters}
                    className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
                  >
                    Clear filter.
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        )}

        {/* --- DATA FOUND: GRAPHS AND TABLES --- */}
        {processedData.data.length > 0 && (
          <div
            className={cn(
              "grid grid-cols-1 gap-3 items-start rounded-lg",
              showCharts ? "lg:grid-cols-2 lg:gap-6" : ""
            )}
          >
            {/* Mobile View */}
            {isMobile && (
              <div className="flex flex-col gap-4">
                {showCharts ? (
                  <Card className="bg-white border border-gray-200 shadow-sm rounded-lg mx-0 overflow-hidden">
                    {/* Count / Value Tabs Header */}
                    <div className="flex w-full border-b border-gray-200 bg-white">
                      <button
                        onClick={() => setValueType("count")}
                        className={cn(
                          "flex-1 py-3 text-sm font-medium transition-all relative",
                          valueType === "count" ? "text-sahi-primary-color border-b-2 border-sahi-green" : "text-gray-500"
                        )}
                      >
                        Count
                      </button>
                      <button
                        onClick={() => setValueType("value")}
                        className={cn(
                          "flex-1 py-3 text-sm font-medium transition-all relative",
                          valueType === "value" ? "text-sahi-primary-color border-b-2 border-sahi-green" : "text-gray-500"
                        )}
                      >
                        Value
                      </button>
                    </div>

                    <CardContent className="p-2">
                      <div style={{ width: '100%', height: `${dynamicHeight}px` }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={(processedData?.chartData || []).map((item) => ({
                                name: item.product,
                                value: valueType === "count" ? (item.count || 0) : (item.totalValue || 0),
                                percentage: parseFloat(valueType === "count" ? (item.countPercentage || 0) : (item.valuePercentage || 0)),
                              }))}
                              cx="50%"
                              cy="110"
                              outerRadius={80}
                              innerRadius={55}
                              dataKey="value"
                              paddingAngle={2}
                              isAnimationActive={false}
                              style={{ outline: "none" }}
                            >
                              {(processedData?.chartData || []).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={themeColors[index % themeColors.length]} stroke="none" />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                              formatter={(value, name, props) => [
                                `${valueType === "value" ? currencySymbol : ""}${value?.toLocaleString("en-IN")} (${props?.payload?.percentage?.toFixed(2)}%)`,
                                name,
                              ]}
                            />
                            <Legend
                              verticalAlign="bottom"
                              align="center"
                              content={({ payload }) => (
                                <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 mt-4 px-2 w-full">
                                  {payload.map((entry, index) => {
                                    // CRITICAL SAFETY CHECK: Prevent crash during filter transition
                                    const item = processedData?.chartData?.[index];
                                    if (!item) return null;
                                    return (
                                      <div key={`legend-${index}`} className="flex items-center gap-1.5">
                                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                                        <span className="text-[11px] font-medium text-gray-700">
                                          {entry.value}{" "}
                                          <span className="text-gray-400 font-normal ml-1">
                                            ({(valueType === 'count' ? item.countPercentage : item.valuePercentage) || "0"}%)
                                          </span>
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="flex w-full border-b border-gray-200 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <button
                      onClick={() => setValueType("count")}
                      className={cn(
                        "flex-1 py-3 text-sm font-medium transition-all relative",
                        valueType === "count" ? "text-sahi-primary-color border-b-2 border-sahi-green" : "text-gray-500"
                      )}
                    >
                      Count
                    </button>
                    <button
                      onClick={() => setValueType("value")}
                      className={cn(
                        "flex-1 py-3 text-sm font-medium transition-all relative",
                        valueType === "value" ? "text-sahi-primary-color border-b-2 border-sahi-green" : "text-gray-500"
                      )}
                    >
                      Value
                    </button>
                  </div>
                )}

                <Card className="bg-sahi-white overflow-hidden border border-gray-200 shadow-sm h-fit mb-20">
                  <CardContent className="px-0 py-0">
                    <ReportTable />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Desktop View */}
            {!isMobile && (
              <>
                {/* Table Column - Always show */}
                <div className="flex flex-col">
                  <Card className="bg-sahi-white !pt-0 overflow-hidden border border-gray-200 shadow-sm h-fit">
                    <CardContent className="px-0 py-0 !sahi-radius">
                      <ReportTable />
                    </CardContent>
                  </Card>

                  {/* Pagination Component - Desktop Only */}
                  {/* <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(processedData.data.length / rowsPerPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(size) => setRowsPerPage(size)}
                    onPageChange={(page) => setCurrentPage(page)}
                  /> */}
                </div>

                {showCharts && (
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      {
                        title: "Count Distribution",
                        dataKey: "count",
                        shareKey: "countPercentage",
                        type: "count"
                      },
                      {
                        title: "Value Distribution",
                        dataKey: "totalValue",
                        shareKey: "valuePercentage",
                        type: "value"
                      }
                    ].map((chart) => (
                      <Card key={chart.type} className="bg-white border border-gray-200 shadow-sm rounded-lg flex flex-col h-full">

                        {/* TIGHT HEADER: Minimal padding to stay close to graph */}
                        <CardHeader className="pt-3 pb-0 px-3 bg-transparent text-center">
                          <CardTitle className="text-sm font-semibold text-gray-700">
                            {chart.title}
                          </CardTitle>
                        </CardHeader>

                        <CardContent className="p-0 flex flex-col">              {/* GRAPH: Negative margin-top to reduce space from header */}
                          <div className="w-full h-[220px] -mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                <Pie
                                  data={processedData.chartData.map((item) => ({
                                    name: item.product,
                                    value: item[chart.dataKey],
                                    percentage: parseFloat(item[chart.shareKey]),
                                  }))}
                                  cx="50%"
                                  cy="47%" // Centering slightly lower to balance the -mt-4
                                  outerRadius={75}
                                  innerRadius={50}
                                  paddingAngle={2}
                                  dataKey="value"
                                  isAnimationActive={false}
                                >
                                  {processedData.chartData.map((_, idx) => (
                                    <Cell
                                      key={`cell-${idx}`}
                                      fill={themeColors[idx % themeColors.length]}
                                      stroke="none"
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                  formatter={(val, name, props) => [
                                    `${chart.type === 'value' ? currencySymbol : ''}${val.toLocaleString("en-IN")} (${props.payload.percentage.toFixed(2)}%)`,
                                    name,
                                  ]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>

                          {/* DYNAMIC CIRCULAR LEGEND: Minimal gap from graph */}
                          <div className="px-4 pb-4 -mt-2">
                            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
                              {processedData.chartData.map((item, idx) => (
                                <div key={`leg-${idx}`} className="flex items-center gap-1.5">
                                  {/* Circle Dot */}
                                  <div
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: themeColors[idx % themeColors.length] }}
                                  />
                                  <span className="text-[11px] font-medium text-gray-600 leading-none">
                                    {item.product}
                                    <span className="text-gray-400 ml-1 font-normal">
                                      ({parseFloat(item[chart.shareKey]).toFixed(1)}%)
                                    </span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ProductDemandReport;